import React, { useEffect, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { PrevBtn, NextBtn, PaginationWrapper } from "../Styles/PageStyles";
import Images from "../../Images";
import tw from "tailwind-styled-components";
import moment from "moment";
import Config, { PaginationLimit } from "../../Config";
import { capitalizeFirstLetter, truncateText } from "../../helpers";
import { IoMdEye } from "react-icons/io";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import Tooltip from "../Tooltip";
import { MdOutlineChevronRight } from "react-icons/md";

const CommonAreaTable = ({
    apiData = [],
    setSelectedData,
    setIsViewDetails,
}) => {
    const storageKey = `housekeeping_active_page`;
    const [data, setData] = useState([]);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [selectedRow, setSelectedRow] = useState(null);
    const storageValue = sessionStorage.getItem(storageKey);
    const savedPage = storageValue !== null ? Number(storageValue) : 0;
    const [currentPage, setCurrentPage] = useState(savedPage);

    useEffect(() => {
        setCurrentPage(savedPage);
    }, [savedPage]);

    const viewDetails = (item) => {
        setIsViewDetails(true);
        setSelectedData(item);
    };

    // ---------- Cell renderers ----------

    // First column: only property name now
    const getPropertyCell = (item) => (
        <span
            className="font-medium text-gray-800 text-sm truncate"
            title={item?.propName}
        >
            {capitalizeFirstLetter(truncateText(item?.propName ?? "-", 30))}
        </span>
    );

    const getStatusCell = (item) => {
        const isClean = !!item?.id;

        return isClean ? (
            <Cleaned>
                <span className="inline-flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                    Cleaned
                </span>
            </Cleaned>
        ) : (
            <NotCleaned>
                <span className="inline-flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                    Not Cleaned
                </span>
            </NotCleaned>
        );
    };

    const getDoneByCell = (item) => (
        <span className="text-sm text-gray-600">
            {item?.staffName ? capitalizeFirstLetter(item.staffName) : "--"}
        </span>
    );

    const getTimeCell = (item) => (
        <span className="text-sm text-gray-600">
            {item?.taskDate ? moment(item.taskDate).format("hh:mm A") : "--"}
        </span>
    );

    const ActionBtns = (item) => {
        const isClean = !!item?.id;

        return (
            <Tooltip label={isClean ? "View details" : "No details available"}>
                <button
                    onClick={() => {
                        if (isClean) viewDetails(item);
                    }}
                    disabled={!isClean}
                    className={`transition text-gray-400 ${isClean
                        ? "hover:text-gray-700 cursor-pointer"
                        : "opacity-50 cursor-not-allowed"
                        }`}
                >
                    <MdOutlineChevronRight size={22} />
                </button>
            </Tooltip>
        );
    };

    // ---------- Map API → table rows ----------
    useEffect(() => {
        if (apiData) {
            const tempData = apiData.map((item) => ({
                property: getPropertyCell(item),
                status: getStatusCell(item),
                doneBy: getDoneByCell(item),
                time: getTimeCell(item),
                action: ActionBtns(item),
            }));
            setData(tempData);
        }
    }, [apiData]);

    useEffect(() => {
        const handleResize = () => setIsMobileView(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // ---------- Columns ----------
    const columns = React.useMemo(() => {
        if (isMobileView) {
            return [
                { Header: "Property", accessor: "property" },
                { Header: "Status", accessor: "status" },
                { Header: "", accessor: "action" },
            ];
        }
        return [
            { Header: "Property", accessor: "property" },
            { Header: "Status", accessor: "status" },
            { Header: "Done By", accessor: "doneBy" },
            { Header: "Time", accessor: "time" },
            { Header: "", accessor: "action" },
        ];
    }, [isMobileView]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        pageOptions,
        page,
        state: { pageIndex },
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
    } = useTable(
        {
            data,
            columns,
            initialState: { pageIndex: currentPage, pageSize: PaginationLimit },
        },
        useSortBy,
        usePagination,
    );

    useEffect(() => {
        sessionStorage.setItem(storageKey, pageIndex);
    }, [pageIndex, storageKey]);

    return (
        <>
            <CustomTable {...getTableProps()}>
                <Thead>
                    {headerGroups.map((headerGroup) => (
                        <Tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <Th
                                    {...column.getHeaderProps(
                                        column.getSortByToggleProps(),
                                    )}
                                >
                                    {column.render("Header")}
                                    <span>
                                        {column.isSorted ? (
                                            <img
                                                src={Images.Arrow}
                                                alt="down arrow"
                                                className={`${column.isSortedDesc
                                                    ? "-rotate-90"
                                                    : "rotate-90"
                                                    } w-1.5 inline-block ml-1.5`}
                                            />
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody
                    {...getTableBodyProps()}
                    hasdata={(apiData?.length > 0).toString()}
                >
                    {page.map((row, rowIndex) => {
                        prepareRow(row);
                        const pageItem = apiData[pageIndex * PaginationLimit + rowIndex];
                        if (!pageItem) return null;

                        return (
                            <React.Fragment key={rowIndex}>
                                <Tr
                                    {...row.getRowProps({
                                        style: { height: "80px" },
                                    })}
                                    hasdata={(apiData?.length > 0).toString()}
                                >
                                    {row.cells.map((cell) => (
                                        <Td {...cell.getCellProps()}>
                                            {cell.render("Cell")}
                                        </Td>
                                    ))}
                                </Tr>

                                {selectedRow === rowIndex && window.innerWidth < 768 && (
                                    <Tr>
                                        <Td colSpan={columns.length}>
                                            <ul className="flex flex-col gap-2">
                                                <li>
                                                    <strong>Done by</strong>{" "}
                                                    {data[rowIndex].doneBy}
                                                </li>
                                                <li>
                                                    <strong>Time</strong>{" "}
                                                    {data[rowIndex].time}
                                                </li>

                                                <li className="flex gap-2">
                                                    <strong>Action</strong>{" "}
                                                    {data[rowIndex].action}
                                                </li>
                                            </ul>
                                        </Td>
                                    </Tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </Tbody>
            </CustomTable>

            {apiData?.length > 0 && (
                <PaginationWrapper>
                    <div className="px-2">
                        Page{" "}
                        <em>
                            {pageIndex + 1} of {pageOptions.length}
                        </em>
                    </div>

                    <div className="flex gap-1">
                        <PrevBtn
                            onClick={() => previousPage()}
                            disabled={!canPreviousPage}
                        >
                            <img
                                src={Images.Arrow}
                                alt="arrow"
                                className="w-2 opacity-75 "
                            />
                        </PrevBtn>
                        <NextBtn
                            onClick={() => nextPage()}
                            disabled={!canNextPage}
                        >
                            <img
                                src={Images.Arrow}
                                alt="arrow"
                                className="w-2 rotate-180 opacity-75"
                            />
                        </NextBtn>
                    </div>
                </PaginationWrapper>
            )}
        </>
    );
};

const CustomTable = tw.table` w-full text-gray-600`;
const Thead = tw.thead`border-b uppercase text-gray-600 bg-blue-50`;
const Tbody = tw.tbody`${(props) =>
    props.hasdata === "true" ? "border-b" : ""}`;
const Tr = tw.tr`
  border-b
  hover:bg-slate-50
  transition
`;
const Th = tw.th`text-left text-sm md:p-3 p-0.5 font-normal text-gray-600`;
const Td = tw.td`p-3 text-sm md:p-3 text-left`;

const Cleaned = tw.div`
text-green-500 text-sm font-semibold
`;
const NotCleaned = tw.div`
  text-rose-500 font-semibold text-sm 
`;

export default CommonAreaTable;